import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaSyncAlt, FaSort, FaSortUp, FaSortDown, FaPlus } from 'react-icons/fa';
import CommonHeader from '../../util_components/common_header';
import Paginator from '../../util_components/paginator';
import DateRangeFilter from './DateRangeFilter';
import MultiSelectDropdown from './MultiSelectDropdown';
import GenCheckpointCard from './GenCheckpointCard';
import CustomRuleCard from './CustomRuleCard';
import { new_custom_rule } from './gen_config_utils';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import { image_error_replace } from '../../../util_functions';
import * as actions from '../../../redux/action_creators';

// Status filter tabs. "Pending" includes drafts (everything not submitted).
const STATUS_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Drafts', value: 'Drafts' },
  { label: 'Submitted', value: 'Submitted' }
];

// Checkpoint quick-filter tabs. Value is the check_type ('All' = no filter).
const CHECK_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Day 8', value: 'DAY_8' },
  { label: 'Day 15', value: 'DAY_15' },
  { label: 'Day 22', value: 'DAY_22' },
  { label: 'Week 7', value: 'WEEK_7' },
  { label: 'Week 11', value: 'WEEK_11' },
  { label: 'Custom', value: 'CUSTOM' }
];

// check_type → URL-friendly slug used by the standalone form page (?checkin=…).
const TYPE_TO_CHECKIN = {
  DAY_8: 'day8',
  DAY_15: 'day15',
  DAY_22: 'day22',
  WEEK_7: 'week7',
  WEEK_11: 'week11'
};

// Each row is one member at one checkpoint (Day 8 / 15 / 22, Week 7 / 11).
class EarlyRetentionChecklist extends Component {
  state = {
    items: [],
    loading: false,
    gen_config: [],
    custom_rules: [],
    gen_meta: {},
    editing_rule: null,
    saving_config: false,
    config_dirty: false,
    status_filter: 'Pending',
    check_filter: 'All',
    date_from: '',
    date_to: '',
    agent_filter: [],
    sort_key: 'checkin_date',
    sort_dir: 'asc',
    page: 1
  };

  page_size = 20;

  componentDidMount() {
    this.load_data();
    this.load_gen_config();
  }

  load_gen_config = () => {
    const url = get_api_url('/v2/early_retention_checklist/generation_config');
    get_api(url, true)
      .then((res) => {
        const d = res.data.data || {};
        this.setState({
          gen_config: d.checkpoints || [],
          custom_rules: d.custom_rules || [],
          gen_meta: d.meta || {},
          editing_rule: null,
          config_dirty: false
        });
      })
      .catch(() => {
        /* config is optional context; failure is non-blocking */
      });
  };

  // Update a checkpoint's mode (and Day 15's "previous check-in" statuses)
  // locally only; persistence is via the Save button.
  set_gen_mode = (check_type, mode, statuses = []) => {
    this.setState((prev) => ({
      gen_config: prev.gen_config.map((c) =>
        c.check_type === check_type ? { ...c, mode, value: mode, statuses } : c
      ),
      config_dirty: true
    }));
  };

  // Update a checkpoint's "No" sub-conditions (Day 22 / Week 7 / Week 11).
  set_gen_conditions = (check_type, conditions) => {
    this.setState((prev) => ({
      gen_config: prev.gen_config.map((c) =>
        c.check_type === check_type ? { ...c, conditions } : c
      ),
      config_dirty: true
    }));
  };

  add_custom_rule = () => {
    this.setState((prev) => ({
      custom_rules: [...prev.custom_rules, new_custom_rule()],
      editing_rule: prev.custom_rules.length,
      config_dirty: true
    }));
  };

  update_custom_rule = (idx, rule) => {
    this.setState((prev) => ({
      custom_rules: prev.custom_rules.map((r, i) => (i === idx ? rule : r)),
      config_dirty: true
    }));
  };

  delete_custom_rule = (idx) => {
    this.setState((prev) => ({
      custom_rules: prev.custom_rules.filter((_, i) => i !== idx),
      editing_rule: null,
      config_dirty: true
    }));
  };

  toggle_edit_rule = (idx) => {
    this.setState((prev) => ({ editing_rule: prev.editing_rule === idx ? null : idx }));
  };

  // Persist the whole generation config (explicit Save).
  save_gen_config = () => {
    const checkpoints = {};
    this.state.gen_config.forEach((c) => {
      checkpoints[c.check_type] = {
        mode: c.mode,
        statuses: c.statuses || [],
        conditions: c.conditions || null
      };
    });
    this.setState({ saving_config: true });
    const url = get_api_url('/v2/early_retention_checklist/generation_config');
    post_api(url, { config: { checkpoints, custom_rules: this.state.custom_rules } }, true)
      .then(() => {
        this.props.set_notification_variable(true, 'success', 'Generation config saved');
        this.setState({ saving_config: false, editing_rule: null });
        // Reload so server-assigned rule ids (new rules) land in state.
        this.load_gen_config();
      })
      .catch((e) => {
        let err = 'Failed to save config';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ saving_config: false });
      });
  };

  load_data = () => {
    this.setState({ loading: true });
    const url = get_api_url('/v2/early_retention_checklist/list');
    get_api(url, true)
      .then((res) => {
        this.setState({ items: res.data.data.items || [], loading: false });
      })
      .catch((e) => {
        let err = 'Failed to load checklists';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false });
      });
  };

  // Link to the standalone form page for a row. Standard checkpoints are keyed
  // by student + checkpoint; custom rows by their uuid (a (student, checkin)
  // pair doesn't identify them). from=list tells the form page to show
  // "Back to checklist" / route back here.
  form_link = (item) => {
    if (item.check_type === 'CUSTOM') {
      return `/early-retention-checklist-form?uuid=${encodeURIComponent(item.uuid)}&from=list`;
    }
    return `/early-retention-checklist-form?student_uuid=${encodeURIComponent(
      item.student_uuid
    )}&checkin=${TYPE_TO_CHECKIN[item.check_type] || ''}&from=list`;
  };

  // Filter changes reset to the first page so the user isn't stranded on an
  // out-of-range page.
  set_status_filter = (status_filter) => this.setState({ status_filter, page: 1 });

  set_check_filter = (check_filter) => this.setState({ check_filter, page: 1 });

  matches_check = (item) =>
    this.state.check_filter === 'All' || item.check_type === this.state.check_filter;

  set_date_range = ({ from, to }) => this.setState({ date_from: from, date_to: to, page: 1 });

  clear_date_range = () => this.setState({ date_from: '', date_to: '', page: 1 });

  set_agent_filter = (agent_filter) => this.setState({ agent_filter, page: 1 });

  set_page = (page) => this.setState({ page });

  // Distinct ops agents a checklist is assigned to, for the "Assigned to" filter.
  agent_options = () => {
    const seen = {};
    this.state.items.forEach((i) => {
      if (i.assigned_uuid && !seen[i.assigned_uuid]) {
        seen[i.assigned_uuid] = i.assigned_name || i.assigned_uuid;
      }
    });
    return Object.keys(seen)
      .map((uuid) => ({ value: uuid, label: seen[uuid] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  matches_agent = (item) => {
    const { agent_filter } = this.state;
    if (agent_filter.length === 0) return true;
    return agent_filter.includes(item.assigned_uuid);
  };

  matches_status = (item) => {
    switch (this.state.status_filter) {
      case 'Pending':
        return item.status === 'pending' || item.status === 'draft';
      case 'Drafts':
        return item.status === 'draft';
      case 'Submitted':
        return item.status === 'submitted';
      default:
        return true;
    }
  };

  matches_date = (item) => {
    const { date_from, date_to } = this.state;
    if (!date_from && !date_to) return true;
    const iso = item.checkin_date_iso;
    if (!iso) return false;
    if (date_from && iso < date_from) return false;
    if (date_to && iso > date_to) return false;
    return true;
  };

  // Click a sortable column header: toggle direction if same key, else asc.
  toggle_sort = (sort_key) =>
    this.setState((prev) => ({
      sort_key,
      sort_dir: prev.sort_key === sort_key && prev.sort_dir === 'asc' ? 'desc' : 'asc',
      page: 1
    }));

  // Comparable value per sortable column.
  sort_value = (item, key) => {
    switch (key) {
      case 'member':
        return (item.student_name || '').toLowerCase();
      case 'check':
        // Order checkpoints Day 8 < 15 < 22 < Week 7 < Week 11 < Custom.
        if (item.check_type === 'CUSTOM') return 200;
        return item.week_number > 0 ? 100 + item.week_number : item.day_number;
      case 'checkin_date':
      default:
        return item.checkin_date_iso || '';
    }
  };

  get_filtered = () => {
    const { sort_key, sort_dir } = this.state;
    const rows = this.state.items.filter(
      (i) =>
        this.matches_status(i) &&
        this.matches_check(i) &&
        this.matches_date(i) &&
        this.matches_agent(i)
    );
    const dir = sort_dir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const av = this.sort_value(a, sort_key);
      const bv = this.sort_value(b, sort_key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  };

  check_badge = (item) => {
    if (item.check_type === 'CUSTOM') {
      // Hover shows the full rule name ("Custom: <condition> + <filters>").
      return (
        <span
          className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-state-info-bg text-state-info-text"
          title={item.custom_name || 'Custom'}
        >
          Custom
        </span>
      );
    }
    const text = item.week_number > 0 ? `Week ${item.week_number}` : `Day ${item.day_number}`;
    return (
      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-state-info-bg text-state-info-text">
        {text}
      </span>
    );
  };

  status_badge = (status) => {
    const map = {
      draft: { cls: 'bg-state-warning-bg text-state-warning-text', label: 'Draft' },
      submitted: { cls: 'bg-state-success-bg text-state-success-text', label: 'Submitted' },
      pending: { cls: 'bg-surface-sunken text-content-secondary', label: 'Pending' }
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  // "Overall Status of Member" (Red / Yellow / Green) denormalised from the
  // checklist. Empty until an overall status has been picked on the form.
  member_status_badge = (status) => {
    const map = {
      Red: '#dc2626',
      Yellow: '#d97706',
      Green: '#16a34a'
    };
    const bg = map[status];
    if (!bg) return <span className="text-content-muted">—</span>;
    return (
      <span
        className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
        style={{ background: bg }}
      >
        {status}
      </span>
    );
  };

  // Dark segmented tabs (matches the platform's status-filter style).
  render_segmented = (options, selected, on_select) => (
    <div className="inline-flex rounded-full" style={{ background: '#1e293b', padding: 2 }}>
      {options.map((o) => {
        const is_sel = selected === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => on_select(o.value)}
            style={{
              padding: '2px 10px',
              fontSize: 11,
              fontWeight: 600,
              border: 'none',
              borderRadius: 9999,
              cursor: 'pointer',
              background: is_sel ? '#ffffff' : 'transparent',
              color: is_sel ? '#1e293b' : '#cbd5e1'
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );

  // Clickable, sortable column header. Always shows a sort affordance: a muted
  // double-arrow when inactive, an amber up/down when it's the active sort.
  render_sort_th = (label, key) => {
    const active = this.state.sort_key === key;
    const asc = this.state.sort_dir === 'asc';
    let Icon = FaSort;
    let color = 'var(--text-muted)';
    if (active) {
      Icon = asc ? FaSortUp : FaSortDown;
      color = 'var(--state-warning)';
    }
    return (
      <th
        className="text-left px-5 py-2 font-medium cursor-pointer select-none hover:bg-surface-sunken transition-colors"
        onClick={() => this.toggle_sort(key)}
        title="Sort"
      >
        <span className="inline-flex items-center gap-1.5">
          <span style={active ? { color: 'var(--state-warning)' } : undefined}>{label}</span>
          <Icon size={12} color={color} style={{ verticalAlign: 'middle' }} />
        </span>
      </th>
    );
  };

  render_row = (item) => {
    const submitted = item.status === 'submitted';
    return (
      <tr key={item.uuid} className="border-t border-line hover:bg-surface-sunken">
        <td className="text-left px-5 py-3">
          <div className="flex items-center gap-3">
            <img
              src={item.profile_photo}
              onError={image_error_replace}
              alt=""
              className="h-9 w-9 rounded-full object-cover bg-surface-sunken"
            />
            {item.student_uuid ? (
              <Link
                to={`/studentprofile?id=${encodeURIComponent(item.student_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:underline"
              >
                {item.student_name || item.student_uuid}
              </Link>
            ) : (
              <span className="font-medium text-content-primary">{item.student_name || '—'}</span>
            )}
          </div>
        </td>
        <td className="text-left px-5 py-3">{this.member_status_badge(item.overall_status)}</td>
        <td className="text-left px-5 py-3 whitespace-nowrap text-content-secondary">
          {item.checkin_date || '—'}
        </td>
        <td className="text-left px-5 py-3">{this.check_badge(item)}</td>
        <td className="text-left px-5 py-3">{this.status_badge(item.status)}</td>
        <td className="text-left px-5 py-3 whitespace-nowrap text-content-secondary">
          {item.assigned_name || '—'}
        </td>
        <td className="text-left px-5 py-3">
          <Link
            to={this.form_link(item)}
            className="inline-block px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            {submitted ? 'View' : 'Open Form'}
          </Link>
        </td>
      </tr>
    );
  };

  render_body = (rows) => {
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="px-5 py-8 text-center text-content-muted">
            No checklists match these filters.
          </td>
        </tr>
      );
    }
    return rows.map(this.render_row);
  };

  // Custom-rule builder block: each rule is a card with an inline editor; the
  // rules persist with the same Save button as the checkpoint cards.
  render_custom_rules = () => {
    const { custom_rules, gen_meta, saving_config, editing_rule } = this.state;
    return (
      <div className="px-5 pb-4">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold">Custom checklists</h3>
          <span className="text-xs text-content-muted">
            Generated daily for members matching a rule (30-day cooldown per member per rule)
          </span>
        </div>
        <div className="flex flex-col gap-2">
          {custom_rules.map((rule, idx) => (
            <CustomRuleCard
              // New rules have no id yet; fall back to a position-based key.
              key={rule.id || `new-${idx}`}
              rule={rule}
              meta={gen_meta}
              saving={saving_config}
              editing={editing_rule === idx}
              onChange={(r) => this.update_custom_rule(idx, r)}
              onDelete={() => this.delete_custom_rule(idx)}
              onToggleEdit={() => this.toggle_edit_rule(idx)}
            />
          ))}
          {custom_rules.length === 0 ? (
            <div className="text-xs text-content-muted">No custom rules yet.</div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={this.add_custom_rule}
          disabled={saving_config}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-line bg-surface-card text-content-secondary hover:border-line-emphasis disabled:opacity-50 transition-colors"
        >
          <FaPlus size={10} /> Add custom rule
        </button>
      </div>
    );
  };

  // Generation config: one card per checkpoint (mode dropdown + sub-conditions
  // on Day 22 / Week 7 / Week 11) and the custom-rule builder below, all
  // persisted together via the Save button.
  render_config_section = () => {
    const { gen_config, gen_meta, saving_config, config_dirty } = this.state;
    // Checklist generation config is restricted to SUPER_USER.
    if (this.props?.access_type?.toUpperCase() !== 'SUPER_USER') return null;
    if (!gen_config.length) return null;
    return (
      <section
        className="bg-surface-card rounded-lg shadow-sm border border-line"
        style={{ margin: '20px 5%' }}
      >
        <div className="px-5 py-3 border-b border-line flex items-center gap-2">
          <h2 className="font-semibold">Checklist generation</h2>
          <span className="text-xs text-content-muted">
            When the daily job creates each checkpoint
          </span>
          {config_dirty ? (
            <span className="text-xs font-medium text-amber-600">Unsaved changes</span>
          ) : null}
        </div>
        <div className="px-5 py-4 flex flex-col gap-2">
          {gen_config.map((cp) => (
            <GenCheckpointCard
              key={cp.check_type}
              checkpoint={cp}
              meta={gen_meta}
              saving={saving_config}
              onModeChange={this.set_gen_mode}
              onConditionsChange={this.set_gen_conditions}
            />
          ))}
        </div>
        {this.render_custom_rules()}
        <div className="px-5 pb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={this.save_gen_config}
            disabled={saving_config || !config_dirty}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving_config ? 'Saving…' : 'Save'}
          </button>
          {config_dirty && !saving_config ? (
            <span className="text-xs text-content-muted">You have unsaved changes</span>
          ) : null}
        </div>
      </section>
    );
  };

  render() {
    const { loading, date_from, date_to, agent_filter, status_filter, check_filter } = this.state;
    const rows = this.get_filtered();
    const page_limit = Math.max(1, Math.ceil(rows.length / this.page_size));
    const page = Math.min(this.state.page, page_limit);
    const paged_rows = rows.slice((page - 1) * this.page_size, page * this.page_size);

    return (
      <div>
        <CommonHeader title="Early Retention Checklist" />

        {this.render_config_section()}

        <section
          className="bg-surface-card rounded-lg shadow-sm border border-line"
          style={{ margin: '20px 5%' }}
        >
          <div className="px-5 py-3 border-b border-line">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold">Checklist</h2>
              <span className="text-xs text-content-muted">
                {rows.length} {rows.length === 1 ? 'item' : 'items'}
              </span>
              <button
                type="button"
                onClick={this.load_data}
                disabled={loading}
                aria-label="Refresh checklists"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 28,
                  width: 28,
                  borderRadius: 9999,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  padding: 0
                }}
              >
                <FaSyncAlt size={13} color="#f59e0b" className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <DateRangeFilter
                from={date_from}
                to={date_to}
                onChange={this.set_date_range}
                onClear={this.clear_date_range}
              />
              <MultiSelectDropdown
                label="Assigned to"
                options={this.agent_options()}
                selected={agent_filter}
                onChange={this.set_agent_filter}
                placeholder="All assignees"
              />
              {this.render_segmented(STATUS_FILTERS, status_filter, this.set_status_filter)}
              {this.render_segmented(CHECK_FILTERS, check_filter, this.set_check_filter)}
            </div>
          </div>

          {loading && this.state.items.length === 0 ? (
            // Initial load: only the list area shows a placeholder — the rest
            // of the page (config section, filters) stays interactive. On
            // refreshes the table stays visible and the refresh icon spins.
            <div className="px-5 py-10 text-center text-sm text-content-muted">
              Loading checklists…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-sunken text-content-secondary">
                  <tr>
                    {this.render_sort_th('Member', 'member')}
                    <th className="text-left px-5 py-2 font-medium">Member Health</th>
                    {this.render_sort_th('Checkin date', 'checkin_date')}
                    {this.render_sort_th('Check', 'check')}
                    <th className="text-left px-5 py-2 font-medium">Checklist Status</th>
                    <th className="text-left px-5 py-2 font-medium">Assigned To</th>
                    <th className="text-left px-5 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>{this.render_body(paged_rows)}</tbody>
              </table>
            </div>
          )}

          {page_limit > 1 ? (
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line">
              <span className="text-xs text-content-muted">
                Showing {(page - 1) * this.page_size + 1}–
                {Math.min(page * this.page_size, rows.length)} of {rows.length}
              </span>
              <Paginator curr_page={page} page_limit={page_limit} pageChange={this.set_page} />
            </div>
          ) : null}
        </section>
      </div>
    );
  }
}

EarlyRetentionChecklist.propTypes = {
  access_type: PropTypes.string,
  set_notification_variable: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  access_type: state.home.access_type
});
const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(EarlyRetentionChecklist);
